import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import AckSign, { SignaturePad } from './AckSign';
import { parseCSV, downloadCsv } from '../utils/csv';

// ── Material Acknowledgement ───────────────────────────────────────────────
// Three things live here:
//   Issue        — hand an asset to a staff member (creates the custody record)
//   Pending      — assignments with no acknowledgement yet
//   Agreements   — the acknowledgements themselves, with preview PDF
//   Template     — the wording, editable and versioned (admin only)
//
// The acknowledgement is a transaction attached to an assignment; nothing here
// creates a second copy of an asset.

const STATUS = {
  pending:        ['Not sent',      'var(--text2)'],
  link_generated: ['Link ready',    'var(--amber)'],
  sent:           ['Sent',          'var(--amber)'],
  viewed:         ['Opened',        'var(--amber)'],
  acknowledged:   ['Acknowledged',  'var(--green)'],
  expired:        ['Link expired',  'var(--red)'],
  cancelled:      ['Cancelled',     'var(--text3)'],
};

function Badge({ status }) {
  const [label, colour] = STATUS[status] || [status, 'var(--text2)'];
  return (
    <span style={{ fontSize: 11.5, fontWeight: 700, color: colour,
                   border: `1px solid ${colour}`, borderRadius: 999,
                   padding: '2px 9px', whiteSpace: 'nowrap' }}>{label}</span>
  );
}

// Choosing which form an item is acknowledged on. A laptop gets the full
// agreement; a projector remote, tablet, pen drive or headset gets the short
// receipt. Guessing on the server was how an acknowledgement ended up on the
// wrong document, so IT picks — with the fitting form offered first.
function FormPicker({ title, assetType, templates, onPick, onClose, busy }) {
  const suits = t => (t.asset_kinds || []).some(k =>
    String(assetType || '').toLowerCase().includes(String(k).toLowerCase()));
  const ordered = [...templates].sort((a, b) => (suits(b) ? 1 : 0) - (suits(a) ? 1 : 0));
  const [pick, setPick] = useState(String(ordered[0]?.id || ''));

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {!ordered.length ? (
          <div style={{ fontSize: 13.5, color: 'var(--text2)' }}>
            No published template yet. Create one under <b>Template</b> and publish it.
          </div>
        ) : (
          <>
            {assetType && (
              <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 10 }}>
                Item type: <b style={{ color: 'var(--text)' }}>{assetType}</b>
              </div>
            )}
            {ordered.map(t => (
              <label key={t.id} className="opt"
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px',
                         marginBottom: 8, minHeight: 44, cursor: 'pointer',
                         background: String(pick) === String(t.id) ? 'var(--bg3)' : 'transparent',
                         border: `1px solid ${String(pick) === String(t.id)
                           ? 'var(--accent)' : 'var(--border)'}`,
                         borderRadius: 'var(--radius)' }}>
                <input type="radio" name="form" value={t.id} checked={String(pick) === String(t.id)}
                  onChange={e => setPick(e.target.value)} style={{ marginTop: 3 }} />
                <span>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{t.name}</span>
                  {suits(t) && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)',
                                   marginLeft: 8 }}>suits this item</span>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                    {t.layout === 'receipt'
                      ? 'Short receipt — one responsibility paragraph'
                      : `Full agreement — ${t.clause_count} clauses`}
                    {' · v'}{t.version_no}
                    {(t.asset_kinds || []).length ? ` · for ${t.asset_kinds.join(', ')}` : ''}
                  </div>
                </span>
              </label>
            ))}
          </>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!pick || busy}
            onClick={() => onPick(pick)}>{busy ? 'Working…' : 'Continue'}</button>
        </div>
      </div>
    </div>
  );
}

// Type-ahead picker, used for both staff and assets. A campus can have
// hundreds of either, and finding one in a native <select> means scrolling a
// list you cannot search.
//
//   label(item)  — the bold line
//   sub(item)    — the grey line underneath
//   terms(item)  — every string worth matching against what was typed
function SearchPicker({ items, value, onPick, placeholder, label, sub, terms, empty }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);

  useEffect(() => {
    const away = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const chosen = items.find(s => String(s.id) === String(value));
  const term = q.trim().toLowerCase();
  const hits = !term ? items.slice(0, 20) : items.filter(s =>
    terms(s).filter(Boolean).some(v => String(v).toLowerCase().includes(term))).slice(0, 20);

  const choose = s => { onPick(String(s.id)); setQ(''); setOpen(false); };

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <input
        value={open ? q : (chosen ? label(chosen) : q)}
        placeholder={placeholder}
        onChange={e => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => { setQ(''); setOpen(true); setHi(0); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, hits.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter' && open && hits[hi]) { e.preventDefault(); choose(hits[hi]); }
          else if (e.key === 'Escape') setOpen(false);
        }} />
      {chosen && !open && (
        <button type="button" onClick={() => { onPick(''); setQ(''); }}
          title="Clear"
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                   background: 'none', border: 'none', cursor: 'pointer',
                   color: 'var(--text2)', fontSize: 15 }}>×</button>
      )}
      {open && (
        <div className="card" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)',
              maxHeight: 260, overflowY: 'auto', zIndex: 60, padding: 0,
              boxShadow: '0 10px 30px rgba(14,27,71,0.18)' }}>
          {!hits.length && (
            <div style={{ padding: '14px 12px', fontSize: 13, color: 'var(--text2)' }}>
              {q ? `Nothing matches “${q}”.` : (empty || 'Nothing to choose from.')}
            </div>
          )}
          {hits.map((s, i) => {
            const subLine = sub(s);
            return (
              <button key={s.id} type="button" onMouseEnter={() => setHi(i)}
                onClick={() => choose(s)}
                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none',
                         cursor: 'pointer', padding: '9px 12px',
                         background: i === hi ? 'var(--bg3)' : 'none',
                         borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{label(s)}</div>
                {subLine && (
                  <div style={{ fontSize: 11.5,
                                color: subLine.warn ? 'var(--red)' : 'var(--text2)' }}>
                    {subLine.text || subLine}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Opening /api/…/pdf in a tab would drop the Authorization header, so the file
// is fetched as a blob and handed to the browser from memory.
async function openPdf(path, filename) {
  const r = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
  if (filename) {
    const el = document.createElement('a');
    el.href = url; el.download = filename; el.click();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function MaterialAck() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('agreements');
  const [msg, setMsg] = useState(null);

  const flash = (text, type = 'ok') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Material Acknowledgement</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 3 }}>
          Issue an asset, then have the staff member acknowledge custody of it.
        </div>
      </div>

      <div className="flex-gap" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['agreements', 'Agreements'], ['issue', 'Issue an asset'],
          ['pending', 'Issued assets'], ['import', 'Bulk upload'],
          ...(isAdmin ? [['template', 'Template']] : [])].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-secondary'}`}>
            {label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={msg.type === 'ok' ? '' : 'err-msg'}
             style={msg.type === 'ok'
               ? { color: 'var(--green)', fontSize: 13, marginBottom: 12 }
               : { marginBottom: 12 }}>{msg.text}</div>
      )}

      {tab === 'import'     && <BulkImport flash={flash} />}
      {tab === 'agreements' && <Agreements flash={flash} />}
      {tab === 'issue'      && <IssueAsset user={user} flash={flash} onDone={() => setTab('pending')} />}
      {tab === 'pending'    && <Pending flash={flash} onCreated={() => setTab('agreements')} />}
      {tab === 'template'   && <Templates flash={flash} />}
    </div>
  );
}

// ── Bulk upload ─────────────────────────────────────────────────────────────
// Paper acknowledgements being renewed digitally. The file carries the staff
// member, the material and the campus; the portal supplies the asset record,
// the custody row and the acknowledgement number.
const SAMPLE_CSV =
  'campus,staff_name,staff_email,employee_id,designation,item,brand,model,serial_number,' +
  'quantity,accessories,condition,approx_value,issued_on,asset_tag,remarks\n' +
  'Chennai Main,Priya G,priya.g@tipsglobeducate.com,TIPS-TC-9802,Class teacher,' +
  'Projector remote,Benq,,,1,,Good,1000,2026-08-12,,Paper copy dated 12-08-2026\n' +
  'Chennai Main,Arun S,arun.s@tipsglobeducate.com,,ICT Teacher,' +
  'Pen drive,SanDisk,32GB,,2,,Good,600,2026-07-01,,\n' +
  'CBE Main,Ramesh K,,,Lab Assistant,' +
  'Headset,Logitech,H111,,1,,Fair,450,2026-05-20,,No email on record\n';

function BulkImport({ flash }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');

  useEffect(() => {
    api.get('/material-ack/templates').then(r => {
      const pub = (r.data || []).filter(t => t.published && t.active);
      setTemplates(pub);
      if (pub.length) setTemplateId(String(pub[0].id));
    }).catch(() => {});
  }, []);

  const take = text => {
    const { rows: parsed } = parseCSV(text);
    if (!parsed.length) { flash('No rows found in that file', 'err'); return; }
    setRows(parsed); setPreview(null);
  };

  const onFile = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const fr = new FileReader();
    fr.onload = () => take(String(fr.result));
    fr.readAsText(file);
  };

  // The dry run is the real import, rolled back — so what it reports is what
  // will actually happen, not a second guess at it.
  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post('/material-ack/import',
        { rows, commit: false, template_id: templateId || null });
      setPreview(r.data);
    } catch (e) { flash(e.response?.data?.error || 'Could not check the file', 'err'); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!window.confirm(
      `Import ${preview.created} acknowledgement(s)?\n\n`
      + 'Assets will be created for any material not already in the register, and each '
      + 'acknowledgement is created as "Not sent" — you then send the links.')) return;
    setBusy(true);
    try {
      const r = await api.post('/material-ack/import',
        { rows, commit: true, template_id: templateId || null });
      if (r.data.committed) {
        flash(`${r.data.created} acknowledgement(s) imported. Send the links from Agreements.`);
        setRows([]); setPreview(null); setFileName('');
      } else {
        setPreview(r.data);
        flash(r.data.rolled_back_because || 'Nothing was imported', 'err');
      }
    } catch (e) { flash(e.response?.data?.error || 'Import failed', 'err'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
          Renew paper acknowledgements digitally
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Upload what you have — staff, material and campus. The portal creates the asset
          record, the custody entry and the acknowledgement number, then you send each
          person a link to sign. Your paper copies stay as the old record.
        </div>

        <div className="flex-gap" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm"
            onClick={() => downloadCsv('material_acknowledgement_sample.csv', SAMPLE_CSV)}>
            Download sample CSV
          </button>
          <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
            Choose CSV file
            <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: 'none' }} />
          </label>
          {fileName && (
            <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>
              {fileName} — {rows.length} row(s)
            </span>
          )}
          {templates.length > 1 && (
            <>
              <label htmlFor="imptmpl" style={{ fontSize: 13, color: 'var(--text2)' }}>Form</label>
              <select id="imptmpl" value={templateId} onChange={e => setTemplateId(e.target.value)}
                style={{ maxWidth: 260 }}>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.layout === 'receipt' ? 'receipt' : 'agreement'})
                  </option>
                ))}
              </select>
            </>
          )}
          {!!rows.length && (
            <button className="btn btn-secondary btn-sm" onClick={check} disabled={busy}>
              {busy ? 'Checking…' : 'Check the file'}
            </button>
          )}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 12 }}>
          <b>campus</b>, <b>staff_name</b> and <b>item</b> are required. Everything else is
          optional: staff_email (needed to send a link), employee_id, designation, brand,
          model, serial_number, quantity, accessories, condition, approx_value,
          issued_on (YYYY-MM-DD, the date on the paper copy), asset_tag (only if the item is
          already in the register), remarks.
        </div>
      </div>

      {preview && (
        <div className="card">
          <div className="flex-gap" style={{ gap: 14, alignItems: 'center', flexWrap: 'wrap',
                                             marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              {preview.errors
                ? `${preview.errors} row(s) need fixing`
                : `${preview.created} row(s) ready to import`}
              {!!preview.skipped && (
                <span style={{ fontWeight: 400, color: 'var(--text2)' }}>
                  {' '}· {preview.skipped} already in the portal
                </span>
              )}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>
              Checked {preview.total} row(s) — nothing has been saved yet.
            </span>
            <span style={{ flexGrow: 1 }} />
            <button className="btn btn-primary btn-sm" disabled={busy || !!preview.errors}
              onClick={doImport}>
              {busy ? 'Importing…' : `Import ${preview.created}`}
            </button>
          </div>

          {!!preview.errors && (
            <div style={{ fontSize: 12.5, color: 'var(--red)', marginBottom: 10 }}>
              Nothing is imported until every row is valid — a half-finished import is
              impossible to tell apart from a complete one.
            </div>
          )}

          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg3)', textAlign: 'left' }}>
                  {['Row', 'Staff', 'Item', 'Outcome'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', fontWeight: 600,
                                         color: 'var(--text2)', position: 'sticky', top: 0,
                                         background: 'var(--bg3)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.results.map((x, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 12px', color: 'var(--text2)' }}>{x.line}</td>
                    <td style={{ padding: '9px 12px' }}>{x.staff || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>{x.item || '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {x.status === 'error'
                        ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>{x.message}</span>
                        : x.status === 'skipped'
                        ? <span style={{ color: 'var(--text2)' }}>{x.message}</span>
                        : <span>
                            <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                              {x.ack_code}
                            </span>
                            {x.note && (
                              <span style={{ color: 'var(--text2)' }}> · {x.note}</span>
                            )}
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Agreements ──────────────────────────────────────────────────────────────
function Agreements({ flash }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(null);

  const load = useCallback(() => {
    api.get('/material-ack').then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async row => {
    if (!window.confirm(`Cancel ${row.ack_code}?\n\nThe staff member will not be able to sign it.`)) return;
    try { await api.post(`/material-ack/${row.id}/cancel`); flash('Cancelled'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not cancel', 'err'); }
  };

  // Delete is for something created by mistake. A signed acknowledgement is
  // refused by the server; the asset issue itself stays, and reappears under
  // "Awaiting acknowledgement".
  const del = async row => {
    if (!window.confirm(
      `Delete ${row.ack_code}?\n\nThis removes the acknowledgement and its audit trail. `
      + `The asset stays issued to ${row.staff_name}, and will show again under `
      + '"Awaiting acknowledgement".')) return;
    try { await api.delete(`/material-ack/${row.id}`); flash(`${row.ack_code} deleted.`); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not delete', 'err'); }
  };

  // A link IT can hand over by any other means — WhatsApp, a printed QR, or
  // reading it out. Needed whenever email cannot carry it: the staff member
  // has no address, or SMTP is down. Same one-time token either way.
  const copyLink = async row => {
    if (!window.confirm(
      `Generate a link for ${row.staff_name} and copy it?\n\n`
      + 'Any link sent earlier stops working immediately.')) return;
    try {
      const r = await api.post(`/material-ack/${row.id}/link`, {});
      try { await navigator.clipboard.writeText(r.data.url); flash('Link copied to the clipboard.'); }
      catch { window.prompt('Copy this link:', r.data.url); }
      load();
    } catch (e) { flash(e.response?.data?.error || 'Could not generate a link', 'err'); }
  };

  const send = async row => {
    if (!row.staff_email) {
      flash(`${row.staff_name} has no email on record — add it in IT Resources → Staff.`, 'err');
      return;
    }
    const resend = row.status !== 'pending';
    if (!window.confirm(
      `${resend ? 'Send a NEW link' : 'Send the acknowledgement link'} to ${row.staff_email}?`
      + (resend ? '\n\nAny link sent earlier stops working immediately.' : ''))) return;
    setSending(row.id);
    try {
      const r = await api.post(`/material-ack/${row.id}/send`);
      flash(`Sent to ${r.data.sent_to}.`);
      load();
    } catch (e) { flash(e.response?.data?.error || 'Could not send', 'err'); }
    finally { setSending(null); }
  };

  if (!rows) return <div style={{ color: 'var(--text2)' }}>Loading…</div>;
  if (!rows.length) return (
    <div className="card" style={{ color: 'var(--text2)', fontSize: 13.5 }}>
      No acknowledgements yet. Issue an asset, then create one from
      <b> Awaiting acknowledgement</b>.
    </div>
  );

  return (
    <>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg3)', textAlign: 'left' }}>
                {['Reference', 'Staff', 'Asset', 'Issued', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '11px 12px', fontWeight: 600,
                                       color: 'var(--text2)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '11px 12px' }}>
                    <span className="mono" style={{ fontSize: 12.5, color: 'var(--accent)' }}>{r.ack_code}</span>
                  </td>
                  <td style={{ padding: '11px 12px' }}>
                    <div style={{ fontWeight: 600 }}>{r.staff_name || '—'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                      {r.staff_email || 'no email on record'}
                    </div>
                  </td>
                  <td style={{ padding: '11px 12px' }}>
                    <div>{[r.brand, r.model].filter(Boolean).join(' ') || '—'}</div>
                    <div className="mono" style={{ fontSize: 11.5, color: 'var(--text2)' }}>{r.asset_tag}</div>
                  </td>
                  <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>{r.assigned_date}</td>
                  <td style={{ padding: '11px 12px' }}><Badge status={r.status} /></td>
                  <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setOpen(r.id)}>Open</button>{' '}
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => openPdf(`/material-ack/${r.id}/pdf`)}>PDF</button>{' '}
                    {r.status !== 'acknowledged' && r.status !== 'cancelled' && (
                      <>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => setPreview(r.id)}>Preview</button>{' '}
                        <button className="btn btn-primary btn-sm" disabled={sending === r.id}
                          onClick={() => send(r)}>
                          {sending === r.id ? 'Sending…' : r.status === 'pending' ? 'Send link' : 'Resend'}
                        </button>{' '}
                        <button className="btn btn-secondary btn-sm" onClick={() => copyLink(r)}
                          title="Generate a link and copy it, instead of emailing">Copy link</button>{' '}
                        <button className="btn btn-secondary btn-sm" onClick={() => cancel(r)}>Cancel</button>{' '}
                        <button className="btn btn-secondary btn-sm" onClick={() => del(r)}
                          style={{ color: 'var(--red)' }}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {open && <AckDetail id={open} onClose={() => setOpen(null)} />}
      {preview && <EmployeePreview id={preview} onClose={() => setPreview(null)} />}
    </>
  );
}

// Requirement 7: show IT the exact employee page, clearly marked, with signing
// disabled and the one-time link untouched.
function EmployeePreview({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/material-ack/${id}/employee-view`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.error || 'Could not load the preview'));
  }, [id]);

  return (
    <div className="modal-overlay" style={{ alignItems: 'flex-start', overflowY: 'auto' }}>
      <div className="modal" style={{ maxWidth: 900, padding: 0, background: 'transparent',
                                      border: 'none', maxHeight: 'none' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex',
                      justifyContent: 'flex-end', padding: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close preview</button>
        </div>
        {err && <div className="err-msg">{err}</div>}
        {data && <AckSign token="preview" preview previewData={data} />}
      </div>
    </div>
  );
}

function AckDetail({ id, onClose }) {
  const { can } = useAuth();
  const canHr = can('ack_hr', 'view');
  const [d, setD] = useState(null);
  const [itSig, setItSig] = useState(null);
  const [signing, setSigning] = useState(false);
  const [sigErr, setSigErr] = useState('');

  const load = useCallback(() => {
    api.get(`/material-ack/${id}`).then(r => setD(r.data)).catch(() => setD(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // The HR address is remembered in this browser: it is nearly always the same
  // person, and retyping it on every agreement is how typos reach real inboxes.
  const [hrEmail, setHrEmail] = useState(() => {
    try { return localStorage.getItem('ack_hr_email') || ''; } catch { return ''; }
  });
  const [hrBusy, setHrBusy] = useState(false);
  const [hrMsg, setHrMsg] = useState(null);
  const [hrSig, setHrSig] = useState(null);

  const signAsHr = async () => {
    setHrBusy(true); setHrMsg(null);
    try {
      await api.post(`/material-ack/${id}/hr-sign`, { signature_image: hrSig });
      setHrSig(null); load();
    } catch (e) { setHrMsg({ ok: false, text: e.response?.data?.error || 'Could not sign' }); }
    finally { setHrBusy(false); }
  };

  const sendHr = async send => {
    setHrBusy(true); setHrMsg(null);
    try {
      const r = await api.post(`/material-ack/${id}/hr-link`, { email: hrEmail, send });
      if (send) {
        try { localStorage.setItem('ack_hr_email', hrEmail); } catch {}
        setHrMsg({ ok: true, text: `Sent to ${r.data.sent_to}.` });
      } else {
        try { await navigator.clipboard.writeText(r.data.url); setHrMsg({ ok: true, text: 'Link copied.' }); }
        catch { window.prompt('Copy this link for HR:', r.data.url); }
      }
      load();
    } catch (e) { setHrMsg({ ok: false, text: e.response?.data?.error || 'Could not send' }); }
    finally { setHrBusy(false); }
  };

  const doItSign = async () => {
    setSigning(true); setSigErr('');
    try {
      await api.post(`/material-ack/${id}/it-sign`, { signature_image: itSig });
      setItSig(null); load();
    } catch (e) { setSigErr(e.response?.data?.error || 'Could not save the signature'); }
    finally { setSigning(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <span className="modal-title">{d ? d.ack_code : 'Acknowledgement'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div>
          {!d ? <div style={{ color: 'var(--text2)' }}>Loading…</div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))',
                            gap: 12, marginBottom: 16 }}>
                {[['Employee', d.snapshot.employee.name],
                  ['Employee ID', d.snapshot.employee.employee_id || 'not captured yet'],
                  ['Email', d.snapshot.employee.email],
                  ['Contact', d.snapshot.employee.phone || 'not captured yet'],
                  ['Asset', d.snapshot.asset.make_model],
                  ['Serial / Tag', [d.snapshot.asset.serial, d.snapshot.asset.tag].filter(Boolean).join(' / ')],
                  ['Accessories', d.snapshot.asset.accessories || '—'],
                  ['Issued', d.snapshot.asset.issue_date]].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{k}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 8 }}>
                Audit trail
              </div>
              {(d.trail || []).map((t, i) => (
                <div key={i} className="flex-gap"
                     style={{ gap: 10, fontSize: 12.5, padding: '6px 0',
                              borderBottom: '1px solid var(--border)' }}>
                  <span className="mono" style={{ color: 'var(--text2)', width: 110 }}>{t.at}</span>
                  <span style={{ fontWeight: 600, flexGrow: 1 }}>{t.action.replace(/_/g, ' ')}</span>
                  <span style={{ color: 'var(--text2)' }}>{t.actor_label || t.actor_type}</span>
                </div>
              ))}

              {/* The department's own signature on the form, drawn the same
                  way the employee drew theirs. Only after they have signed:
                  IT confirms a handover that has been acknowledged. */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                              letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 8 }}>
                  IT signature
                </div>
                {d.it_signed_at ? (
                  <div className="flex-gap" style={{ gap: 12, alignItems: 'center' }}>
                    {d.it_signature_image && (
                      <img src={d.it_signature_image} alt="IT signature"
                        style={{ height: 46, background: '#fff', border: '1px solid var(--border)',
                                 borderRadius: 'var(--radius)', padding: 3 }} />
                    )}
                    <span style={{ fontSize: 13 }}>
                      Signed by <b>{d.it_signed_name}</b>
                      <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{d.it_signed_display}</div>
                    </span>
                  </div>
                ) : !d.signed ? (
                  <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
                    You can sign once {d.snapshot.employee.name.split(' ')[0]} has acknowledged it.
                  </div>
                ) : (
                  <>
                    <SignaturePad onChange={setItSig} />
                    {sigErr && <div className="err-msg" style={{ marginTop: 8 }}>{sigErr}</div>}
                    <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
                      disabled={!itSig || signing} onClick={doItSign}>
                      {signing ? 'Saving…' : 'Sign as IT'}
                    </button>
                  </>
                )}
              </div>

              {/* HR signs by one-time link — they have no portal account.
                  Only the agreement has an HR row; a material receipt is
                  between the employee and IT. */}
              {d.snapshot.template.layout !== 'receipt' && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 8 }}>
                    HR signature
                  </div>
                  {d.hr_signed_at ? (
                    <div className="flex-gap" style={{ gap: 12, alignItems: 'center' }}>
                      {d.hr_signature_image && (
                        <img src={d.hr_signature_image} alt="HR signature"
                          style={{ height: 46, background: '#fff', border: '1px solid var(--border)',
                                   borderRadius: 'var(--radius)', padding: 3 }} />
                      )}
                      <span style={{ fontSize: 13 }}>
                        Signed by <b>{d.hr_signed_name}</b>
                        <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{d.hr_signed_display}</div>
                      </span>
                    </div>
                  ) : !d.signed ? (
                    <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
                      HR signs after the employee has acknowledged it.
                    </div>
                  ) : !d.it_signed_at ? (
                    <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
                      HR signs after IT. Sign above first, then send this to HR.
                    </div>
                  ) : (
                    <>
                      {/* Someone holding the HR-signature module can sign here
                          and now; everyone else sends the one-time link. */}
                      {canHr && (
                        <div style={{ marginBottom: 14 }}>
                          <SignaturePad onChange={setHrSig} />
                          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }}
                            disabled={!hrSig || hrBusy} onClick={signAsHr}>
                            {hrBusy ? 'Saving…' : 'Sign as HR'}
                          </button>
                          <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 8 }}>
                            Or send it to someone else to sign:
                          </div>
                        </div>
                      )}
                      <div className="flex-gap" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input value={hrEmail} onChange={e => setHrEmail(e.target.value)}
                          placeholder="HR email address" style={{ maxWidth: 280 }} />
                        <button className="btn btn-primary btn-sm" disabled={hrBusy}
                          onClick={() => sendHr(true)}>
                          {hrBusy ? 'Sending…' : 'Send to HR'}
                        </button>
                        <button className="btn btn-secondary btn-sm" disabled={hrBusy}
                          onClick={() => sendHr(false)}
                          title="Generate a link to share another way">Copy link</button>
                      </div>
                      {d.hr_sent_to && (
                        <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 6 }}>
                          Last sent to {d.hr_sent_to}. Sending again replaces that link.
                        </div>
                      )}
                      {hrMsg && <div style={{ fontSize: 12.5, marginTop: 8,
                        color: hrMsg.ok ? 'var(--green)' : 'var(--red)' }}>{hrMsg.text}</div>}
                    </>
                  )}
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => openPdf(`/material-ack/${id}/pdf`)}>
                  {d.signed ? 'Download signed PDF' : 'Preview PDF'}
                </button>
                {!d.signed && (
                  <span style={{ fontSize: 12, color: 'var(--text2)', marginLeft: 10 }}>
                    Unsigned — the PDF is watermarked PREVIEW.
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Issue an asset ──────────────────────────────────────────────────────────
function IssueAsset({ user, flash, onDone }) {
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');
  const [assets, setAssets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ asset_id: '', staff_id: '', assigned_on: '',
                               condition_at_issue: 'New', accessories: '', issue_remarks: '',
                               quantity: 1, approx_value: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get('/master/campuses').then(r => {
      setCampuses(r.data);
      const mine = (user?.campus_ids || [])[0];
      setCampusId(String(mine || r.data[0]?.id || ''));
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!campusId) return;
    // Only assets that are not already in someone's hands.
    api.get('/assets', { params: { campus_id: campusId } })
      .then(r => setAssets((r.data.rows || r.data || []).filter(a => !a.staff_id)))
      .catch(() => setAssets([]));
    api.get(`/master/informedby/${campusId}`).then(r => setStaff(r.data)).catch(() => setStaff([]));
  }, [campusId]);

  const submit = async () => {
    if (!f.asset_id || !f.staff_id) { flash('Pick an asset and a staff member', 'err'); return; }
    setBusy(true);
    try {
      await api.post('/material-ack/assignments', { ...f, assigned_on: f.assigned_on || null });
      flash('Asset issued. Create the acknowledgement from “Issued assets”.');
      setF({ asset_id: '', staff_id: '', assigned_on: '', condition_at_issue: 'New',
             accessories: '', issue_remarks: '', quantity: 1, approx_value: '' });
      onDone && onDone();
    } catch (e) { flash(e.response?.data?.error || 'Could not issue the asset', 'err'); }
    finally { setBusy(false); }
  };

  const chosen = staff.find(s => String(s.id) === String(f.staff_id));

  return (
    <div className="card" style={{ maxWidth: 760 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: 14 }}>
        <div className="form-group">
          <label>Campus</label>
          <select value={campusId} onChange={e => setCampusId(e.target.value)}>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Asset (unassigned only)</label>
          <SearchPicker items={assets} value={f.asset_id} onPick={v => set('asset_id', v)}
            placeholder="Type a tag, brand, model or serial…"
            empty="No unassigned assets on this campus."
            label={a => `${a.asset_tag} — ${[a.brand, a.model].filter(Boolean).join(' ') || a.asset_type}`}
            sub={a => [a.asset_type, a.serial_number, a.condition].filter(Boolean).join(' · ')}
            terms={a => [a.asset_tag, a.brand, a.model, a.asset_type, a.serial_number]} />
          {!assets.length && (
            <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 4 }}>
              No unassigned assets on this campus.
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Issue to</label>
          <SearchPicker items={staff} value={f.staff_id} onPick={v => set('staff_id', v)}
            placeholder="Type a name to search…"
            empty="No staff on this campus."
            label={s => s.name}
            sub={s => ({ text: [s.designation, s.email || 'no email on record'].filter(Boolean).join(' · '),
                         warn: !s.email })}
            terms={s => [s.name, s.designation, s.email, s.employee_id]} />
          {chosen && !chosen.email && (
            <div style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 4 }}>
              No email on record — they cannot be sent an acknowledgement link.
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Issue date</label>
          <input type="date" value={f.assigned_on} onChange={e => set('assigned_on', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Condition at issue</label>
          <select value={f.condition_at_issue} onChange={e => set('condition_at_issue', e.target.value)}>
            {['New', 'Good', 'Fair', 'Refurbished'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Charger / accessories</label>
          <input value={f.accessories} onChange={e => set('accessories', e.target.value)}
            placeholder="65W Type-C charger, bag" />
        </div>
        {/* Both appear on the short receipt used for remotes, tablets, pen
            drives and headsets. The laptop agreement ignores them. */}
        <div className="form-group">
          <label>Quantity</label>
          <input type="number" min="1" value={f.quantity}
            onChange={e => set('quantity', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Approximate value (Rs.)</label>
          <input type="number" min="0" step="1" value={f.approx_value}
            onChange={e => set('approx_value', e.target.value)} placeholder="e.g. 1000" />
        </div>
      </div>
      <div className="form-group">
        <label>Remarks</label>
        <input value={f.issue_remarks} onChange={e => set('issue_remarks', e.target.value)}
          placeholder="Anything noted at handover" />
      </div>
      <button className="btn btn-primary" onClick={submit} disabled={busy}>
        {busy ? 'Issuing…' : 'Issue asset'}
      </button>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 10 }}>
        This records who holds the asset and from when. The acknowledgement is created next.
      </div>
    </div>
  );
}

// ── Awaiting acknowledgement ────────────────────────────────────────────────
function Pending({ flash, onCreated }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [returning, setReturning] = useState(null);
  const [choosing, setChoosing] = useState(null);

  // Every asset currently in someone's hands, not only the ones still needing
  // an acknowledgement — taking an asset back is the other half of this screen.
  const load = useCallback(() => {
    api.get('/material-ack/assignments/active')
      .then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Which wording to use. With one published template this is invisible; with
  // several, leaving the server to guess is how an agreement ends up on the
  // wrong document, so IT picks.
  useEffect(() => {
    api.get('/material-ack/templates')
      .then(r => {
        const pub = (r.data || []).filter(t => t.published && t.active);
        setTemplates(pub);
      })
      .catch(() => {});
  }, []);

  // Creating always goes through the form picker: which document someone is
  // asked to sign is a decision, not a default.
  const create = async (row, tmplId) => {
    setBusy(row.id);
    try {
      const r = await api.post('/material-ack',
        { assignment_id: row.id, template_id: tmplId || null });
      flash(`${r.data.ack_code} created — preview it, then send the link.`);
      setChoosing(null);
      load(); onCreated && onCreated();
    } catch (e) { flash(e.response?.data?.error || 'Could not create', 'err'); }
    finally { setBusy(null); }
  };

  // Undo an issue entered in error: the asset returns to the pool. A real
  // handover that later comes back is a RETURN, which keeps the history.
  const undo = async row => {
    if (!window.confirm(
      `Undo the issue of ${row.asset_tag} to ${row.staff_name}?\n\n`
      + 'The asset becomes unassigned and this custody record is removed. '
      + 'Use this only when the issue was entered by mistake.')) return;
    setBusy(row.id);
    try { await api.delete(`/material-ack/assignments/${row.id}`); flash('Issue undone.'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not undo', 'err'); }
    finally { setBusy(null); }
  };

  if (!rows) return <div style={{ color: 'var(--text2)' }}>Loading…</div>;
  if (!rows.length) return (
    <div className="card" style={{ color: 'var(--text2)', fontSize: 13.5 }}>
      No assets are currently issued to anyone.
    </div>
  );

  const shown = rows.filter(r =>
    filter === 'all' ? true
    : filter === 'pending' ? !r.ack_id
    : filter === 'unsigned' ? (r.ack_id && !r.ack_signed)
    : r.ack_signed);

  return (
    <>
    <div className="flex-gap" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap',
                                       alignItems: 'center' }}>
      {[['all', `All issued (${rows.length})`],
        ['pending', `No acknowledgement (${rows.filter(r => !r.ack_id).length})`],
        ['unsigned', `Awaiting signature (${rows.filter(r => r.ack_id && !r.ack_signed).length})`],
        ['signed', `Acknowledged (${rows.filter(r => r.ack_signed).length})`]].map(([id, label]) => (
        <button key={id} onClick={() => setFilter(id)}
          className={`btn btn-sm ${filter === id ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
      ))}
    </div>

    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: 'var(--bg3)', textAlign: 'left' }}>
              {['Staff', 'Asset', 'Issued', 'Acknowledgement', ''].map(h => (
                <th key={h} style={{ padding: '11px 12px', fontWeight: 600, color: 'var(--text2)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '11px 12px' }}>
                  <div style={{ fontWeight: 600 }}>{r.staff_name}</div>
                  <div style={{ fontSize: 11.5, color: r.staff_email ? 'var(--text2)' : 'var(--red)' }}>
                    {r.staff_email || 'no email on record'}
                  </div>
                </td>
                <td style={{ padding: '11px 12px' }}>
                  <div>{[r.brand, r.model].filter(Boolean).join(' ') || r.asset_type}</div>
                  <div className="mono" style={{ fontSize: 11.5, color: 'var(--text2)' }}>{r.asset_tag}</div>
                </td>
                <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                  <div>{r.assigned_date}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{r.campus_name}</div>
                </td>
                <td style={{ padding: '11px 12px' }}>
                  {r.ack_id
                    ? <>
                        <Badge status={r.ack_status} />
                        <div className="mono" style={{ fontSize: 11.5, color: 'var(--text2)',
                                                       marginTop: 3 }}>{r.ack_code}</div>
                      </>
                    : <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>not created</span>}
                </td>
                <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                  {!r.ack_id && (
                    <>
                      <button className="btn btn-primary btn-sm" disabled={busy === r.id}
                        onClick={() => setChoosing(r)}>
                        {busy === r.id ? 'Creating…' : 'Create acknowledgement'}
                      </button>{' '}
                    </>
                  )}
                  <button className="btn btn-secondary btn-sm" disabled={busy === r.id}
                    onClick={() => setReturning(r)}>Return / unassign</button>{' '}
                  {/* Undoing is for an issue entered by mistake. Once the
                      employee has signed, the custody period is a fact — the
                      way out is a return, which keeps the history. */}
                  {!r.ack_signed && (
                    <button className="btn btn-secondary btn-sm" disabled={busy === r.id}
                      onClick={() => undo(r)} style={{ color: 'var(--red)' }}>Undo issue</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    {returning && (
      <ReturnAsset row={returning} flash={flash}
        onClose={() => setReturning(null)}
        onDone={() => { setReturning(null); load(); }} />
    )}
    {choosing && (
      <FormPicker title={`Acknowledgement form for ${choosing.asset_tag}`}
        assetType={choosing.asset_type} templates={templates}
        busy={busy === choosing.id}
        onPick={id => create(choosing, id)} onClose={() => setChoosing(null)} />
    )}
    </>
  );
}

// Taking an asset back. The custody row is closed, not deleted — what came
// back, in what state, and what was missing is exactly what a later dispute
// or a No Due check needs.
function ReturnAsset({ row, flash, onClose, onDone }) {
  const [f, setF] = useState({
    returned_on: '', condition_at_return: 'Good',
    accessories_returned: row.accessories || '', missing_accessories: '',
    damage_notes: '', return_remarks: '', send_for_service: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/material-ack/assignments/${row.id}/return`, f);
      flash(`${row.asset_tag} returned by ${row.staff_name}.`);
      onDone();
    } catch (e) { flash(e.response?.data?.error || 'Could not record the return', 'err'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Return {row.asset_tag}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
          Issued to <b style={{ color: 'var(--text)' }}>{row.staff_name}</b> on {row.assigned_date}
          {row.accessories ? <> with <b style={{ color: 'var(--text)' }}>{row.accessories}</b></> : null}.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px,1fr))',
                      gap: 12 }}>
          <div className="form-group">
            <label>Return date</label>
            <input type="date" value={f.returned_on} onChange={e => set('returned_on', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Condition on return</label>
            <select value={f.condition_at_return}
              onChange={e => set('condition_at_return', e.target.value)}>
              {['Good', 'Fair', 'Needs Repair', 'Faulty'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Accessories returned</label>
          <input value={f.accessories_returned}
            onChange={e => set('accessories_returned', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Missing accessories, if any</label>
          <input value={f.missing_accessories}
            onChange={e => set('missing_accessories', e.target.value)}
            placeholder="Leave blank if everything came back" />
        </div>
        <div className="form-group">
          <label>Damage, if any</label>
          <input value={f.damage_notes} onChange={e => set('damage_notes', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Remarks</label>
          <input value={f.return_remarks} onChange={e => set('return_remarks', e.target.value)} />
        </div>

        <label className="opt" style={{ display: 'flex', gap: 8, alignItems: 'center',
                                        fontSize: 13, marginBottom: 6 }}>
          <input type="checkbox" checked={f.send_for_service}
            onChange={e => set('send_for_service', e.target.checked)} />
          <span>Send for service instead of returning it to stock</span>
        </label>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
          The asset becomes {f.send_for_service ? 'Sent for service' : 'Stock'} and is free to
          issue again. The custody record is kept in the asset's history.
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Record return'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Template manager ────────────────────────────────────────────────────────
function Templates({ flash }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get('/material-ack/templates').then(r => setList(r.data)).catch(() => setList([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (starter, layout = 'agreement') => {
    const name = window.prompt('Template name',
      !starter ? '' : layout === 'receipt' ? 'Material Receipt (general items)' : 'Staff Laptop Agreement');
    if (!name) return;
    // Which item types this form suits, so it is offered first for them.
    const kinds = layout === 'receipt'
      ? window.prompt('Which item types is this for? (comma separated)',
          'Projector remote, Tablet, Pen drive, Headset')
      : window.prompt('Which item types is this for? (comma separated)', 'Laptop');
    try {
      const r = await api.post('/material-ack/templates', {
        name, starter, layout,
        asset_kinds: String(kinds || '').split(',').map(s => s.trim()).filter(Boolean),
      });
      flash(starter
        ? `Created from the ${layout === 'receipt' ? 'material receipt' : 'TIPS laptop agreement'}`
          + ' — review and publish it.'
        : 'Blank template created.');
      load(); setOpenId(r.data.id);
    } catch (e) { flash(e.response?.data?.error || 'Could not create', 'err'); }
  };

  // Only a template nothing was ever issued against can go. One that has been
  // used is named by signed documents, so the server refuses and suggests
  // making it inactive instead.
  const remove = async t => {
    if (!window.confirm(`Delete the template “${t.name}”?`)) return;
    try { await api.delete(`/material-ack/templates/${t.id}`); flash('Template deleted.'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not delete', 'err'); }
  };

  if (openId) return <TemplateEditor id={openId} flash={flash}
    onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div>
      <div className="flex-gap" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" onClick={() => create(true, 'agreement')}>
          Start from the laptop agreement
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => create(true, 'receipt')}>
          Start from the material receipt
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => create(false, 'agreement')}>
          Blank
        </button>
      </div>

      {!list ? <div style={{ color: 'var(--text2)' }}>Loading…</div>
        : !list.length ? (
          <div className="card" style={{ fontSize: 13.5, color: 'var(--text2)' }}>
            No template yet. Start from the TIPS laptop agreement — the full wording is
            pre-loaded and you can edit any clause before publishing.
          </div>
        ) : list.map(t => (
          <div key={t.id} className="card" style={{ marginBottom: 10, display: 'flex',
                alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{t.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                {t.layout === 'receipt' ? 'Receipt' : 'Agreement'} · v{t.version_no}
                {t.layout === 'receipt' ? '' : ` · ${t.clause_count} clauses`}
                {' · '}{t.published ? 'published' : 'draft'}
                {(t.asset_kinds || []).length ? ` · for ${t.asset_kinds.join(', ')}` : ''}
              </div>
            </div>
            <div className="flex-gap" style={{ gap: 8 }}>
              <button className="btn btn-secondary btn-sm"
                onClick={() => openPdf(`/material-ack/templates/${t.id}/preview`)}>Sample PDF</button>
              <button className="btn btn-primary btn-sm" onClick={() => setOpenId(t.id)}>Edit</button>
              <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }}
                onClick={() => remove(t)}>Delete</button>
            </div>
          </div>
        ))}
    </div>
  );
}

function TemplateEditor({ id, flash, onBack }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/material-ack/templates/${id}`).then(r => {
      setD(r.data);
      const c = r.data.current || {};
      setF({ title: c.title || '', intro: c.intro || '', declaration: c.declaration || '',
             signature_note: c.signature_note || '', clauses: c.clauses || [] });
    }).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!f || !d) return <div style={{ color: 'var(--text2)' }}>Loading…</div>;

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setClause = (i, k, v) => setF(s => ({
    ...s, clauses: s.clauses.map((c, j) => j === i ? { ...c, [k]: v } : c) }));
  const move = (i, by) => setF(s => {
    const next = [...s.clauses];
    const j = i + by;
    if (j < 0 || j >= next.length) return s;
    [next[i], next[j]] = [next[j], next[i]];
    return { ...s, clauses: next };
  });

  const save = async publish => {
    if (publish && !window.confirm(
      'Publish this version?\n\nNew acknowledgements will use this wording. '
      + 'Agreements already signed keep the version they were signed against.')) return;
    setBusy(true);
    try {
      await api.put(`/material-ack/templates/${id}/version`, { ...f, publish });
      flash(publish ? 'Published — new acknowledgements will use this wording.' : 'Saved');
      load();
    } catch (e) { flash(e.response?.data?.error || 'Could not save', 'err'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex-gap" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap',
                                         alignItems: 'center' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <b>{d.name}</b>
        <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>
          v{d.current?.version_no} · {d.current?.published ? 'published' : 'draft'}
        </span>
        <span style={{ flexGrow: 1 }} />
        <button className="btn btn-secondary btn-sm"
          onClick={() => openPdf(`/material-ack/templates/${id}/preview`)}>Sample PDF</button>
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => save(false)}>Save draft</button>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => save(true)}>Publish</button>
      </div>

      {d.current?.published && (
        <div className="card" style={{ marginBottom: 12, fontSize: 12.5, color: 'var(--text2)',
                                       borderLeft: '3px solid var(--amber)' }}>
          This version is published. Saving creates <b>v{(d.current.version_no || 1) + 1}</b> —
          agreements already signed keep the wording they were signed against.
        </div>
      )}

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="form-group">
          <label>Document title</label>
          <input value={f.title} onChange={e => set('title', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Opening paragraph</label>
          <textarea rows={3} value={f.intro} onChange={e => set('intro', e.target.value)} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>
          Placeholders you can use anywhere:{' '}
          {(d.fields || []).map(([k]) => (
            <code key={k} style={{ fontSize: 11.5, background: 'var(--bg3)', padding: '1px 5px',
                                   borderRadius: 4, marginRight: 4 }}>{`{{${k}}}`}</code>
          ))}
        </div>
      </div>

      {/* A receipt has no numbered clauses — its single responsibility
          paragraph is the declaration below. Showing an empty clause list
          there would invite someone to add one that never renders. */}
      {d.current?.layout === 'receipt' ? (
        <div className="card" style={{ marginBottom: 12, fontSize: 12.5, color: 'var(--text2)' }}>
          This is a <b>receipt</b>: the opening line above, the item table, and the
          responsibility paragraph below. It has no numbered clauses.
        </div>
      ) : f.clauses.map((c, i) => (
        <div key={i} className="card" style={{ marginBottom: 10 }}>
          <div className="flex-gap" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, color: 'var(--text2)', width: 24 }}>{i + 1}.</span>
            <input value={c.heading} onChange={e => setClause(i, 'heading', e.target.value)}
              placeholder="Clause heading" style={{ flexGrow: 1 }} />
            <button className="btn btn-secondary btn-sm" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
            <button className="btn btn-secondary btn-sm" onClick={() => move(i, 1)}
              disabled={i === f.clauses.length - 1}>↓</button>
            <button className="btn btn-secondary btn-sm"
              onClick={() => window.confirm(`Remove clause ${i + 1}?`) &&
                setF(s => ({ ...s, clauses: s.clauses.filter((_, j) => j !== i) }))}>Remove</button>
          </div>
          <textarea rows={4} value={c.body} onChange={e => setClause(i, 'body', e.target.value)} />
        </div>
      ))}

      {d.current?.layout !== 'receipt' && (
        <button className="btn btn-secondary btn-sm"
          onClick={() => setF(s => ({ ...s, clauses: [...s.clauses, { heading: '', body: '' }] }))}>
          + Add clause
        </button>
      )}

      <div className="card" style={{ marginTop: 14 }}>
        <div className="form-group">
          <label>Employee declaration</label>
          <textarea rows={3} value={f.declaration} onChange={e => set('declaration', e.target.value)} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Note under the signature block</label>
          <textarea rows={2} value={f.signature_note}
            onChange={e => set('signature_note', e.target.value)} />
        </div>
      </div>
    </div>
  );
}
